import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/analytics';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import DeviceMobileMessageIcon from '@/components/icons/DeviceMobileMessageIcon';
import type { ContactVerificationSuccessPayload } from '@/components/ModalRenderer/components/SE/contactVerificationSuccess';
import useOnContactVerificationSuccess from '@/components/ModalRenderer/components/SE/useOnContactVerificationSuccess';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import useCountdown from '@/components/OTPVerification/hooks/useCountdown';
import OTPForm from '@/components/OTPVerification/OTPForm/OTPForm';
import { AnalyticsEvents } from '@/constants/analytics';
import { ErrorIds } from '@/constants/ApiResponse';
import useModal from '@/hooks/useModal/useModal';
import useRequestMobileVerificationCode from '@/hooks/useRequestMobileVerificationCode';
import { formatElapsedDuration } from '@/localization/duration/duration';
import Button from '@/ui-kit/components/Button/Button';
import { parseApiError } from '@/utils/error';

type Payload = ContactVerificationSuccessPayload & { mobile: string; resendAfter: string };

const MobileVerificationOtp = ({
  payload: { mobile, resendAfter, successAction },
  closeModal,
}: ModalProps<Payload>) => {
  const { t } = useTranslation(['Modal', 'FormErrors']);
  const { openModal } = useModal();
  const handleSuccessAction = useOnContactVerificationSuccess(successAction);
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);
  // Keep local expiration: resetting the resend mutation clears its data and would fall back to the
  // already-expired payload resendAfter, re-enabling Resend too early.
  const [resendExpiration, setResendExpiration] = useState(() => new Date(resendAfter));

  const resetFormFields = useCallback(() => {
    setFormKey(prevKey => prevKey + 1);
  }, []);

  const {
    mutate: verifyCode,
    isPending: isVerifyPending,
    error: verifyError,
    reset: resetVerifyMutation,
  } = useMutation({
    mutationFn: API.user.verifyMobileVerificationCode,
    onSuccess: async () => {
      // creditActions may have no active observer on the profile path; refetchType:'all'
      // ensures the cache is fresh before the success modal opens and reads it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.creditActions, refetchType: 'all' }),
      ]);
      openModal('SE.MOBILE_VERIFICATION_SUCCESS', { payload: { successAction } });
    },
    onError: resetFormFields,
  });

  const {
    requestMobileVerificationCode: resendCode,
    isPending: isResendPending,
    isSuccess: isResendSuccess,
    errorMessage: resendErrorMessage,
    reset: resetResendMutation,
  } = useRequestMobileVerificationCode({
    onSuccess: ({ resendAfter: nextResendAfter }) => {
      setResendExpiration(new Date(nextResendAfter));
      resetFormFields();
      resetVerifyMutation();
    },
    onAlreadyVerified: () => {
      closeModal();
      handleSuccessAction();
    },
    onRequest: () => {
      trackEvent(AnalyticsEvents.SE.MOBILE_VERIFICATION_OTP_RESEND);
    },
  });

  const handleVerify = useCallback(
    (verificationCode: string) => {
      resetVerifyMutation();
      resetResendMutation();
      verifyCode(verificationCode);
    },
    [resetVerifyMutation, resetResendMutation, verifyCode],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      if (newCode.length === 0) {
        return;
      }

      // Only reset when an error/info is showing — unconditional reset re-renders forever
      // because OTPForm's onCodeChange effect depends on this callback identity.
      if (isResendSuccess) {
        resetResendMutation();
      }
      if (verifyError) {
        resetVerifyMutation();
      }
    },
    [isResendSuccess, verifyError, resetResendMutation, resetVerifyMutation],
  );

  const resendCooldownSeconds = useCountdown(resendExpiration);
  const isResendDisabled = resendCooldownSeconds > 0 || isResendPending || isVerifyPending;

  const { errorId, metadata } = verifyError ? parseApiError(verifyError) : {};
  const triesLeft = metadata?.codeTriesLeft;
  const isWrongCodeWithTries = errorId === ErrorIds.WRONG_CODE && typeof triesLeft === 'number';
  const isTerminalVerifyError =
    errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED || errorId === ErrorIds.CODE_EXPIRED;
  const canEnterCode = !isTerminalVerifyError;
  const isInvalid = isWrongCodeWithTries || isTerminalVerifyError;

  let errorMessage: string | undefined;
  if (resendErrorMessage) {
    errorMessage = resendErrorMessage;
  } else if (verifyError) {
    if (isWrongCodeWithTries) {
      errorMessage = t('Modal:MobileVerificationOtp.wrongCode', { count: triesLeft });
    } else if (errorId === ErrorIds.CODE_EXPIRED) {
      errorMessage = t('Modal:MobileVerificationOtp.codeExpired');
    } else if (errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED) {
      errorMessage = t('Modal:MobileVerificationOtp.maxTriesReached');
    } else {
      errorMessage = t('FormErrors:somethingWentWrong');
    }
  }

  const infoMessage =
    isResendSuccess && !errorMessage ? t('Modal:MobileVerificationOtp.codeSent') : undefined;

  const resendLabel =
    resendCooldownSeconds > 0
      ? t('Modal:MobileVerificationOtp.resendWithCountdown', {
          duration: formatElapsedDuration(resendCooldownSeconds),
        })
      : t('Modal:MobileVerificationOtp.resend');

  return (
    <Modal title="">
      <div className="-mt-2 mb-6 flex flex-col items-center text-center">
        <DeviceMobileMessageIcon className="text-icon-brand mb-4 size-12" aria-hidden="true" />
        <h2 className="text-heading mb-2 lg:text-center">{t('Modal:MobileVerificationOtp.title')}</h2>
        <div className="text-text-subtle flex flex-col gap-1">
          <p>{t('Modal:MobileVerificationOtp.subtitle')}</p>
          <span data-private>{mobile}</span>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <OTPForm
          key={formKey}
          onSubmit={handleVerify}
          onCodeChange={handleCodeChange}
          isInvalid={isInvalid}
          isDisabled={!canEnterCode || isVerifyPending || isResendPending}
          errorMessage={errorMessage}
          infoMessage={infoMessage}
        />
        <ModalActions>
          <Button
            className="tabular-nums"
            type="button"
            variant="tertiary"
            disabled={isResendDisabled}
            loading={isResendPending}
            onClick={() => resendCode(mobile)}
          >
            {resendLabel}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
};

export default MobileVerificationOtp;
